import React, { useState, useMemo } from 'react';
import { MENU_ITEMS, BOWL_BUILDERS, LOGO_URL } from '../mock';
import { Plus, Star, Users, Leaf, Flame, ChevronDown, Settings2, Info } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import BowlBuilder from './BowlBuilder';

// W360-style section structure
const SECTIONS = [
  {
    id: 'popular',
    label: 'Most Popular',
    note: 'Our most-ordered catering picks.',
    items: ['byo-power', 'fig-feta', 'byo-green', 'greek-salad', 'byo-buddha', 'kale-quinoa-steak'],
  },
  {
    id: 'bowls',
    label: 'Bowls',
    note: 'All catering dishes can be made gluten-free upon request.',
    filterCat: 'bowls',
  },
  {
    id: 'salads',
    label: 'Salads',
    note: 'All catering dishes can be made gluten-free upon request.',
    filterCat: 'salads',
  },
  {
    id: 'wraps',
    label: 'Wraps',
    note: 'If you have dietary restrictions, note them in the special instructions. All catering dishes can be made gluten-free upon request.',
    filterCat: 'wraps',
  },
  {
    id: 'desserts',
    label: 'Desserts',
    note: 'Plant-based and naturally sweetened.',
    filterCat: 'desserts',
  },
  {
    id: 'beverages',
    label: 'Beverages',
    filterCat: 'beverages',
  },
];

const DIET_MAP = {
  Vegetarian: 'VEGETARIAN',
  Vegan: 'VEGAN',
  'Vegan-option': 'VEGAN OPTION',
  'Gluten-free': 'GLUTEN FREE',
  'Refined-sugar-free': 'SUGAR FREE',
  Pescatarian: 'PESCATARIAN',
  'Plant-based': 'PLANT-BASED',
};

function getDietLabels(tags = []) {
  return tags.filter((t) => DIET_MAP[t]).map((t) => DIET_MAP[t]);
}

export default function MenuShowcase({ onAdd }) {
  const [expanded, setExpanded] = useState(null);
  const [builderItem, setBuilderItem] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('popular');

  const itemsBySection = useMemo(() => {
    const map = {};
    SECTIONS.forEach((s) => {
      if (s.items) {
        map[s.id] = s.items
          .map((id) => MENU_ITEMS.find((m) => m.id === id))
          .filter(Boolean);
      } else if (s.filterCat) {
        map[s.id] = MENU_ITEMS.filter((m) => m.category === s.filterCat);
      } else {
        map[s.id] = [];
      }
    });
    return map;
  }, []);

  const openBuilder = (item) => {
    setBuilderItem(item);
    setBuilderOpen(true);
  };

  const handleAdd = (item) => {
    if (BOWL_BUILDERS[item.id]) {
      openBuilder(item);
      return;
    }
    onAdd(item);
    toast.success(`${item.short || item.name} added to order`);
  };

  const handleConfirm = (customized) => {
    for (let i = 0; i < (customized.qty || 1); i += 1) {
      onAdd(customized);
    }
    toast.success(`${customized.short || customized.name} added to order`);
  };

  const jumpTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`section-${id}`);
    if (el) {
      const offset = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  return (
    <section id="menu" className="relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#245A40] font-semibold">Healthy Catering Menu — Chicago</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1B2A22] mt-3 leading-[1.05]">
              Mediterranean bowls, salad wraps <em className="italic">&amp; plant-based trays</em> <br className="hidden sm:block" />made from scratch.
            </h2>
          </div>
          <p className="max-w-md text-[#3F4A42] text-[17px] leading-relaxed">
            Browse the best Mediterranean bowls in Chicago — custom grain bowls, healthy salad wraps and plant-based trays for offices and events. Most items are gluten-free or can be modified to be gluten-free, vegan or vegetarian.
          </p>
        </div>

        {/* W360-style catering policy banner */}
        <div className="bg-[#E6EBD5] border border-[#CFDAB1] rounded-2xl p-5 sm:p-6 mb-10 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="w-11 h-11 rounded-full bg-[#245A40] grid place-items-center shrink-0">
            <Info className="w-5 h-5 text-[#E8C56B]" />
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1.5 text-sm text-[#1B4631] flex-1">
            <PolicyLine k="Notice" v="24 hours required" />
            <PolicyLine k="Minimum order" v="$300" />
            <PolicyLine k="Delivery" v="Free — included on all catering orders" />
            <PolicyLine k="Gluten-free" v="Available on request" />
          </ul>
        </div>

        {/* Section nav pills (sticky-ish) */}
        <div className="sticky top-16 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-2 mb-8 bg-[#F6F2E8]/95 backdrop-blur border-b border-[#D6CFB8]/60">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => jumpTo(s.id)}
                className={`tab-pill shrink-0 px-4 h-9 rounded-full text-xs sm:text-sm font-semibold border uppercase tracking-wider ${
                  activeSection === s.id
                    ? 'bg-[#245A40] text-[#F6F2E8] border-[#245A40]'
                    : 'bg-[#FBF8EF] text-[#1B2A22] border-[#D6CFB8] hover:border-[#245A40]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-16">
          {SECTIONS.map((section) => {
            const list = itemsBySection[section.id] || [];
            if (list.length === 0) return null;
            return (
              <div key={section.id} id={`section-${section.id}`} className="scroll-mt-32">
                <div className="flex items-baseline gap-3 mb-2">
                  <h3 className="font-display text-2xl sm:text-3xl text-[#1B2A22] uppercase tracking-wide">
                    {section.label}
                  </h3>
                  <div className="h-px flex-1 bg-[#D6CFB8]" />
                </div>
                {section.note && (
                  <p className="text-xs sm:text-sm text-[#6F6A55] mb-6">{section.note}</p>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                  {list.map((item) => (
                    <MenuCard
                      key={`${section.id}-${item.id}`}
                      item={item}
                      isExpanded={expanded === `${section.id}-${item.id}`}
                      onToggleExpand={() =>
                        setExpanded(expanded === `${section.id}-${item.id}` ? null : `${section.id}-${item.id}`)
                      }
                      onAdd={() => handleAdd(item)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-14 text-center text-xs text-[#6F6A55]">
          Pricing, modifiers &amp; descriptions sourced from <a className="underline hover:text-[#245A40]" href="https://wholesome360.com/shop" target="_blank" rel="noreferrer">wholesome360.com</a>.
        </p>
      </div>

      <BowlBuilder
        item={builderItem}
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        onConfirm={handleConfirm}
      />
    </section>
  );
}

function PolicyLine({ k, v }) {
  return (
    <li className="flex items-baseline gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-[#245A40]/70 font-bold w-20 sm:w-auto shrink-0">{k}</span>
      <span className="font-semibold">{v}</span>
    </li>
  );
}

function MenuCard({ item, isExpanded, onToggleExpand, onAdd }) {
  const dietLabels = getDietLabels(item.tags);
  const customizable = !!BOWL_BUILDERS[item.id];

  return (
    <article className="menu-card bg-[#FBF8EF] rounded-3xl overflow-hidden border border-[#D6CFB8] flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-[#E6EBD5]">
        <img
          src={item.image}
          alt={`${item.name} — ${{ bowls: 'custom grain bowls catering, best Mediterranean bowls Chicago', wraps: 'healthy salad wraps Chicago', salads: 'healthy salad catering Chicago', desserts: 'plant-based catering desserts Chicago' }[item.category] || 'Wholesome 360 healthy catering Chicago'}`}
          className={item.image === LOGO_URL ? 'w-full h-full object-contain p-10' : 'w-full h-full object-cover'}
          loading="lazy"
        />
        {customizable && (
          <div className="absolute top-3 left-3 bg-[#E0703B] text-[#F6F2E8] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1">
            <Settings2 className="w-3 h-3" /> Customizable
          </div>
        )}
        <div className="absolute top-3 right-3 bg-[#1B2A22]/85 backdrop-blur text-[#F6F2E8] text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
          <Star className="w-3 h-3 fill-[#E8C56B] text-[#E8C56B]" />
          {item.rating}
        </div>
        {item.calories && (
          <div className="absolute bottom-3 left-3 bg-[#F6F2E8]/95 backdrop-blur text-[#1B2A22] text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 border border-[#D6CFB8]">
            <Flame className="w-3 h-3 text-[#E0703B]" /> {item.calories} cal
          </div>
        )}
      </div>

      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <h4 className="font-display text-xl text-[#1B2A22] leading-snug">{item.name}</h4>
        <p className="mt-2 text-sm text-[#3F4A42] line-clamp-3 leading-relaxed">{item.description}</p>

        {item.calories && (
          <div className="mt-3 text-xs font-semibold text-[#1B2A22]">
            Calories <span className="text-[#245A40]">{item.calories}</span>
          </div>
        )}

        {dietLabels.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A55] font-bold mb-1.5">Dietary preferences</div>
            <div className="flex flex-wrap gap-1.5">
              {dietLabels.map((d) => (
                <span
                  key={d}
                  className="text-[10px] uppercase tracking-wider font-bold text-[#245A40] border border-[#245A40]/30 bg-[#E6EBD5] px-2 py-1 rounded-full"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {item.ingredients && item.ingredients.length > 0 && (
          <>
            <button
              onClick={onToggleExpand}
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#245A40] hover:text-[#1B4631] self-start"
              aria-expanded={isExpanded}
            >
              {isExpanded ? 'Hide details' : 'See ingredients & options'}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isExpanded && (
              <ul className="mt-3 bg-[#E6EBD5]/60 rounded-xl p-3 space-y-1.5 border border-[#CFDAB1]">
                {item.ingredients.map((ing, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-xs text-[#1B4631] leading-relaxed">
                    <Leaf className="w-3 h-3 mt-0.5 shrink-0 text-[#245A40]" />
                    <span>{ing}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="flex-1" />

        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-2xl text-[#1B2A22]">${item.price.toFixed(2)}</div>
            <div className="text-[11px] text-[#6F6A55] flex items-center gap-1 mt-0.5">
              <Users className="w-3 h-3" /> {item.serves}
            </div>
          </div>
          <Button onClick={onAdd} className="btn-primary rounded-full h-10 px-4 font-semibold">
            {customizable ? (
              <>
                <Settings2 className="w-4 h-4 mr-1" /> Customize
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" /> Add
              </>
            )}
          </Button>
        </div>
      </div>
    </article>
  );
}
